import {
  ArrowUpRight,
  BookmarkPlus,
  Check,
  ChevronDown,
  CreditCard,
  Info,
  Mail,
  Paperclip,
  Send,
  TextQuote,
  UserCheck,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { Avatar, Chip, CountryCode, HealthBar, KpiTile, Monogram, PlanTag } from '@/components/redesign/bits'
import { NativeSelect } from '@/components/ui/native-select'
import { SearchField } from '@/components/ui/search-field'
import { Segmented } from '@/components/ui/segmented'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/ui/states'
import { PageTitle } from '@/app/layout'
import { useCrumb } from '@/app/shell'
import { useMe } from '@/features/auth/api'
import { useStaff } from '@/features/team/api'
import { errorMessage } from '@/lib/api/errors'
import type { TicketDetail, TicketSummary } from '@/lib/api/types'
import { cn } from '@/lib/cn'
import { formatMinutes, formatMoney, formatRelative, numberFmt } from '@/lib/format'
import { useNow } from '@/lib/use-now'
import {
  type AssignedFilter,
  type SourceFilter,
  type StatusFilter,
  downloadAttachment,
  useAssign,
  useReply,
  useSetStatus,
  useTicket,
  useTickets,
} from './api'
import { formatBytes, sourceLabel, statusLabel } from './format'
import { NewTicketDialog } from './new-ticket-dialog'
import { ReplyTemplateEditor } from './reply-template-editor'
import { ReplyTemplatePicker } from './reply-template-picker'
import { placeholderValues, toTemplate, useReplyTemplates, useTemplateUsed } from './reply-templates'

const COUNTRY = new Intl.DisplayNames(['en'], { type: 'region' })
const countryName = (code: string) => {
  try {
    return COUNTRY.of(code) ?? code
  } catch {
    return code
  }
}

/** Minutes a ticket has waited for the team: amber after 4 hours, red after 12. */
const waitMinutes = (t: TicketSummary, now: number) =>
  Math.max(0, (now - new Date(t.last_message_at).getTime()) / 60000)
const waitTone = (m: number) => (m > 720 ? 'bad' : m > 240 ? 'warn' : '')

const typing = (target: EventTarget | null) =>
  /^(INPUT|TEXTAREA|SELECT)$/.test((target as HTMLElement | null)?.tagName ?? '') ||
  Boolean((target as HTMLElement | null)?.isContentEditable)

/**
 * The support inbox (redesign of 2026-10-16): customers' tickets and website enquiries in a list,
 * the open conversation beside it, and the customer's account beside that. Open tickets from
 * Business accounts (priority support) come first, then those waiting longest. Opening a ticket is
 * recorded in the audit log, so none opens by itself.
 */
export function TicketsPage() {
  const { ticketId } = useParams()
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const status = (params.get('status') as StatusFilter | null) ?? 'open'
  const assigned = (params.get('assigned') as AssignedFilter | null) ?? 'all'
  const source = (params.get('source') as SourceFilter | null) ?? 'all'
  const page = Number(params.get('page') ?? 1)
  const search = params.get('search') ?? ''
  const [draft, setDraft] = useState(search)
  const tickets = useTickets(status, assigned, source, search, page)
  const list = tickets.data?.data ?? []
  const now = useNow()
  const meta = tickets.data?.meta

  const update = (next: Record<string, string>) =>
    setParams((p) => {
      const merged = new URLSearchParams(p)
      for (const [k, v] of Object.entries(next)) {
        if (v === '' || (k === 'status' && v === 'open') || (k !== 'status' && v === 'all')) merged.delete(k)
        else merged.set(k, v)
      }
      if (!('page' in next)) merged.delete('page')
      return merged
    })

  useEffect(() => {
    const t = setTimeout(() => {
      if (draft.trim() !== search) update({ search: draft.trim() })
    }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft])

  const open = (id: string) => void navigate({ pathname: `/tickets/${id}`, search: params.toString() })

  // J and K move through the list, as in most mail apps.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (typing(e.target) || e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key !== 'j' && e.key !== 'k') return
      const i = list.findIndex((t) => t.id === ticketId)
      const next = list[e.key === 'j' ? Math.min(list.length - 1, i + 1) : Math.max(0, i - 1)]
      if (next && next.id !== ticketId) open(next.id)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  const counts = meta?.counts
  return (
    <>
      <PageTitle
        title="Support inbox"
        description="Every question from the app, the website and emails to support@, with the customer’s account beside it. Your reply emails them, and customers also see it in the app."
        actions={
          <>
            {(meta?.email_tickets || (meta?.held_emails ?? 0) > 0) && (
              <Link to="/tickets/held" className="btn">
                <Mail aria-hidden="true" />
                Held emails
                {(meta?.held_emails ?? 0) > 0 && (
                  <span className="cnt">{numberFmt.format(meta?.held_emails ?? 0)}</span>
                )}
              </Link>
            )}
            <Link to="/tickets/templates" className="btn">
              <TextQuote aria-hidden="true" />
              Reply templates
            </Link>
          </>
        }
      />
      <div className="kpis">
        <KpiTile
          label="Open"
          value={numberFmt.format(counts?.open ?? 0)}
          compare={
            counts?.oldest_open_at
              ? `oldest waiting ${formatRelative(counts.oldest_open_at).replace(' ago', '')}`
              : 'nothing waiting'
          }
        />
        <KpiTile
          label="Nobody on it"
          value={numberFmt.format(counts?.unassigned ?? 0)}
          compare={
            counts?.website || counts?.email
              ? [
                  counts.website ? `${counts.website} from the website` : null,
                  counts.email ? `${counts.email} by email` : null,
                ]
                  .filter(Boolean)
                  .join(', ')
              : 'open tickets without an owner'
          }
        />
        <KpiTile
          label="First reply, median"
          value={meta?.first_reply.now != null ? formatMinutes(meta.first_reply.now) : '—'}
          now={meta?.first_reply.now}
          previous={meta?.first_reply.previous}
          invert
          compare="this week"
          spark={meta?.first_reply.series.map((v) => v ?? 0)}
          sparkColor="var(--viz-4)"
        />
        <KpiTile
          label="Resolved this week"
          value={numberFmt.format(meta?.resolved.now ?? 0)}
          now={meta?.resolved.now}
          previous={meta?.resolved.previous}
          compare="vs last week"
          spark={meta?.resolved.series.map((v) => v ?? 0)}
        />
      </div>

      <div className="inbox">
        <div className="ib-list">
          <div className="ib-filters">
            <Segmented
              label="Status"
              value={status}
              onChange={(v) => update({ status: v })}
              options={[
                { value: 'open', label: 'Open', count: counts?.open },
                { value: 'answered', label: 'Waiting on them', count: counts?.answered },
                { value: 'resolved', label: 'Resolved' },
                { value: 'all', label: 'All' },
              ]}
            />
            <div className="bar">
              <Segmented
                label="Assigned to"
                value={assigned}
                onChange={(v) => update({ assigned: v })}
                options={[
                  { value: 'all', label: 'Anyone' },
                  { value: 'me', label: 'Mine', count: counts?.mine },
                  { value: 'unassigned', label: 'Nobody' },
                ]}
              />
              <NativeSelect
                pill
                aria-label="From"
                value={source}
                onChange={(e) => update({ source: e.target.value })}
              >
                <option value="all">From anywhere</option>
                <option value="app">From the app</option>
                <option value="website">Website enquiries</option>
                <option value="email">By email</option>
              </NativeSelect>
            </div>
            <SearchField
              label="Search tickets"
              placeholder="Subject, #number or customer"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="max-w-none"
            />
          </div>
          <div className="ib-items">
            {tickets.isPending ? (
              <Skeleton className="m-3 h-40 rounded-lg" />
            ) : tickets.isError ? (
              <ErrorState error={tickets.error} onRetry={() => void tickets.refetch()} />
            ) : list.length === 0 ? (
              <p className="empty-line" style={{ padding: 20 }}>
                Nothing here. Try another filter.
              </p>
            ) : (
              list.map((t) => (
                <TicketRow key={t.id} t={t} now={now} on={t.id === ticketId} onOpen={() => open(t.id)} />
              ))
            )}
            {meta && meta.last_page > 1 && (
              <div className="t-foot">
                <span>
                  Page {meta.current_page} of {meta.last_page}
                </span>
                <span className="acts">
                  <button
                    type="button"
                    className="btn sm ghost"
                    disabled={meta.current_page <= 1}
                    onClick={() => update({ page: String(meta.current_page - 1) })}
                  >
                    Newer
                  </button>
                  <button
                    type="button"
                    className="btn sm ghost"
                    disabled={meta.current_page >= meta.last_page}
                    onClick={() => update({ page: String(meta.current_page + 1) })}
                  >
                    Older
                  </button>
                </span>
              </div>
            )}
          </div>
        </div>
        {ticketId ? (
          <Conversation key={ticketId} id={ticketId} />
        ) : (
          <div className="grid place-items-center p-10 text-center text-[13.5px] text-ink-subtle">
            <div className="grid max-w-sm gap-2">
              <b className="text-ink">Pick a ticket on the left</b>
              <span>
                The conversation and the customer’s account open here. Opening a ticket is recorded in the
                audit log.
              </span>
              <span className="kbd justify-center">
                <kbd>J</kbd>
                <kbd>K</kbd> move through the list
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

function TicketRow({
  t,
  now,
  on,
  onOpen,
}: {
  t: TicketSummary
  now: number
  on: boolean
  onOpen: () => void
}) {
  const name = t.workspace?.name ?? t.requester?.company ?? t.requester?.name ?? 'Someone'
  const waiting = t.status === 'open' ? waitMinutes(t, now) : 0
  return (
    <button
      type="button"
      className={cn('ti', on && 'on', t.status === 'open' && t.last_from === 'customer' && 'unread')}
      onClick={onOpen}
      aria-current={on ? 'true' : undefined}
    >
      {t.workspace ? (
        <Monogram name={name} size="sm" />
      ) : (
        <Avatar name={t.requester?.name ?? name} size="sm" />
      )}
      <b>{name}</b>
      <span className={cn('wait', t.status === 'open' && waitTone(waiting))}>
        {t.status === 'open' ? formatMinutes(waiting) : t.status === 'answered' ? 'Waiting' : 'Done'}
      </span>
      <span className="subj">
        {t.reference} · {t.subject}
      </span>
      <span className="tags">
        {t.priority && <Chip tone="bad">Priority</Chip>}
        {t.source === 'website' && <Chip tone="lime">Website</Chip>}
        {t.source === 'email' && <Chip tone="line">Email</Chip>}
        {t.plan && <PlanTag plan={t.plan.key} name={t.plan.name} />}
        <span style={{ marginLeft: 'auto' }}>
          <Avatar name={t.assigned?.name} size="xs" />
        </span>
      </span>
    </button>
  )
}

function Conversation({ id }: { id: string }) {
  const ticket = useTicket(id)
  useCrumb(ticket.data ? `${ticket.data.reference} ${ticket.data.subject}` : null)
  if (ticket.isPending) return <Skeleton className="m-4 h-80 rounded-lg" />
  if (ticket.isError) return <ErrorState error={ticket.error} onRetry={() => void ticket.refetch()} />
  return <Thread t={ticket.data} />
}

function Thread({ t }: { t: TicketDetail }) {
  const me = useMe()
  const staff = useStaff()
  const reply = useReply(t.id)
  const assign = useAssign(t.id)
  const setStatus = useSetStatus(t.id)
  const templates = useReplyTemplates()
  const used = useTemplateUsed()
  const [body, setBody] = useState('')
  const [picking, setPicking] = useState(false)
  const [saving, setSaving] = useState(false)
  const [assignOpen, setAssignOpen] = useState(false)
  const [newTicketOpen, setNewTicketOpen] = useState(false)
  const replyRef = useRef<HTMLTextAreaElement>(null)
  const caret = useRef<{ start: number; end: number } | null>(null)
  const fail = (e: unknown) => toast.error(errorMessage(e))
  const values = placeholderValues(t, me.data?.name ?? '')
  const knownCategory = templates.data?.meta.categories.some((c) => c.key === t.category) ? t.category : null

  const resolve = () => {
    if (t.status === 'resolved') return
    setStatus.mutate('resolved', { onSuccess: () => toast.success('Resolved.'), onError: fail })
  }

  // R starts a reply and E resolves an active ticket. Closed tickets stay closed for staff.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (typing(e.target) || e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key === 'r') {
        e.preventDefault()
        replyRef.current?.focus()
      } else if (e.key === 'e') resolve()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  const insertTemplate = (templateId: string, text: string) => {
    const { start, end } = caret.current ?? { start: body.length, end: body.length }
    const before = body.slice(0, start)
    const after = body.slice(end)
    const lead = before === '' || before.endsWith('\n\n') ? '' : before.endsWith('\n') ? '\n' : '\n\n'
    const trail = after === '' || after.startsWith('\n') ? '' : '\n\n'
    setBody(before + lead + text + trail + after)
    setPicking(false)
    used.mutate(templateId)
    const at = (before + lead + text).length
    caret.current = { start: at, end: at }
    requestAnimationFrame(() => {
      replyRef.current?.focus()
      replyRef.current?.setSelectionRange(at, at)
    })
  }

  const send = () =>
    reply.mutate(body.trim(), {
      onSuccess: () => {
        setBody('')
        caret.current = null
        toast.success('Reply sent. The ticket now waits on the customer.')
      },
      onError: fail,
    })

  const people = (staff.data?.data ?? []).filter((s) => !s.disabled)
  const customer = t.workspace ? (t.requester?.name ?? t.workspace.name) : (t.requester?.name ?? 'Them')

  return (
    <div className="ib-conv">
      <div className="conv">
        <div className="conv-h">
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 12.5, color: 'var(--subtle)', marginBottom: 2 }}>
              {t.reference} · {t.category_label} · {sourceLabel[t.source]} · {statusLabel[t.status]}
              {t.status === 'answered' &&
                ' · Auto-resolves 24 hours after our last reply; a later customer reply reopens it.'}
            </div>
            <h2>{t.subject}</h2>
          </div>
          <div className="acts">
            <span className="anchor">
              <button
                type="button"
                className="btn sm"
                onClick={() => setAssignOpen((o) => !o)}
                aria-expanded={assignOpen}
              >
                <Avatar name={t.assigned?.name} size="xs" />
                {t.assigned ? t.assigned.name.split(' ')[0] : 'Assign'}
                <ChevronDown aria-hidden="true" />
              </button>
              {assignOpen && (
                <div className="pop right" role="listbox" aria-label="Assign to">
                  {me.data && t.assigned?.id !== me.data.id && (
                    <button
                      type="button"
                      className="opt"
                      onClick={() => {
                        setAssignOpen(false)
                        assign.mutate(me.data?.id ?? null, { onError: fail })
                      }}
                    >
                      <UserCheck aria-hidden="true" />
                      Assign to me
                    </button>
                  )}
                  {people
                    .filter((s) => s.id !== me.data?.id)
                    .map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className="opt"
                        onClick={() => {
                          setAssignOpen(false)
                          assign.mutate(s.id, {
                            onSuccess: () => toast.success(`Assigned to ${s.name}.`),
                            onError: fail,
                          })
                        }}
                      >
                        <Avatar name={s.name} size="xs" />
                        <span>
                          {s.name}
                          <small>
                            {s.role === 'owner' ? 'Owner' : s.role === 'admin' ? 'Admin' : 'Support'}
                          </small>
                        </span>
                        {t.assigned?.id === s.id && (
                          <span className="cnt">
                            <Check aria-hidden="true" />
                          </span>
                        )}
                      </button>
                    ))}
                  {t.assigned && (
                    <button
                      type="button"
                      className="opt"
                      onClick={() => {
                        setAssignOpen(false)
                        assign.mutate(null, { onError: fail })
                      }}
                    >
                      <Avatar name={null} size="xs" />
                      Nobody
                    </button>
                  )}
                </div>
              )}
            </span>
            {t.status !== 'resolved' && (
              <button type="button" className="btn sm" onClick={resolve} disabled={setStatus.isPending}>
                <Check aria-hidden="true" /> Resolve
              </button>
            )}
          </div>
        </div>
        <ol className="msgs" aria-label="Conversation">
          {t.thread.map((m) => (
            <li key={m.id} className={cn('msg', m.from === 'staff' && 'staff')}>
              <Avatar name={m.name} size="sm" />
              <div className="bub">
                <small>
                  {m.name} · {m.from === 'staff' ? 'Arva SEO' : 'Customer'}
                  {m.created_at ? ` · ${formatRelative(m.created_at)}` : ''}
                </small>
                <span className="whitespace-pre-line">{m.body}</span>
                {m.attachments.length > 0 && (
                  <ul className="mt-2 grid gap-1" aria-label="Files">
                    {m.attachments.map((a) => (
                      <li key={a.id}>
                        <button
                          type="button"
                          className="btn sm ghost"
                          onClick={() => void downloadAttachment(t.id, a.id).catch(fail)}
                        >
                          <Paperclip aria-hidden="true" />
                          {a.filename}
                          <small style={{ color: 'var(--subtle)' }}>{formatBytes(a.size)}</small>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {m.email && <EmailDetails email={m.email} />}
              </div>
            </li>
          ))}
        </ol>
        {t.status === 'resolved' ? (
          <div className="composer">
            <p className="text-sm text-ink-muted">
              This ticket is resolved. Only a customer reply reopens it. For a new update, create a separate
              ticket.
            </p>
            <button type="button" className="btn sm primary" onClick={() => setNewTicketOpen(true)}>
              Create new ticket
            </button>
          </div>
        ) : (
          <form
            className="composer"
            onSubmit={(e) => {
              e.preventDefault()
              if (body.trim().length >= 2) send()
            }}
          >
            <label htmlFor="reply" className="sr-only">
              Reply
            </label>
            <textarea
              id="reply"
              ref={replyRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onSelect={(e) => {
                caret.current = { start: e.currentTarget.selectionStart, end: e.currentTarget.selectionEnd }
              }}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && body.trim().length >= 2) send()
              }}
              maxLength={10000}
              placeholder={`Reply to ${customer.split(' ')[0]}. They get it by email${t.source === 'app' ? ' and in the app' : ''}.`}
            />
            <div className="bar">
              <button type="button" className="btn sm" onClick={() => setPicking(true)}>
                <TextQuote aria-hidden="true" />
                Template
              </button>
              <button
                type="button"
                className="btn sm ghost"
                disabled={body.trim().length < 2 || !templates.data}
                onClick={() => setSaving(true)}
              >
                <BookmarkPlus aria-hidden="true" />
                Save as template
              </button>
              <span className="kbd push">
                <kbd>J</kbd>
                <kbd>K</kbd> move <kbd>R</kbd> reply <kbd>E</kbd> resolve
              </span>
              <button
                type="submit"
                className="btn sm primary"
                disabled={body.trim().length < 2 || reply.isPending}
              >
                <Send aria-hidden="true" />
                Send and wait for them
              </button>
            </div>
          </form>
        )}
        <NewTicketDialog ticket={t} open={newTicketOpen} onOpenChange={setNewTicketOpen} />
        <ReplyTemplatePicker
          open={picking}
          onOpenChange={setPicking}
          category={knownCategory}
          values={values}
          onInsert={(template, text) => insertTemplate(template.id, text)}
        />
        {templates.data && (
          <ReplyTemplateEditor
            open={saving}
            onOpenChange={setSaving}
            meta={templates.data.meta}
            heading="Save as a template"
            note="This ticket’s names and details became placeholders, so the template reads right on any ticket. Check them, give it a title, and the whole team can use it."
            initial={{ title: '', category: knownCategory, body: toTemplate(body.trim(), values) }}
          />
        )}
      </div>
      <CustomerSide t={t} />
    </div>
  )
}

function CustomerSide({ t }: { t: TicketDetail }) {
  const c = t.customer
  const now = useNow()
  if (!c) {
    return (
      <aside className="ctx" aria-label="Who asked">
        <div className="who">
          <Avatar name={t.requester?.name ?? 'Someone'} size="lg" />
          <div>
            <b>{t.requester?.name ?? 'Someone'}</b>
            <small>{t.requester?.company ?? t.requester?.email}</small>
          </div>
        </div>
        {t.requester?.email && (
          <p className="text-[13px] break-all text-ink-muted select-all">{t.requester.email}</p>
        )}
        <p style={{ fontSize: 13, color: 'var(--muted)' }}>
          {t.source === 'website'
            ? 'Not a customer yet. Website enquiries get your reply by email, with a private link to answer.'
            : t.source === 'email'
              ? (t.context?.existing_user
                  ? 'This address has an Arva SEO account, but the email couldn’t be verified as coming from it, so it isn’t linked. '
                  : 'No Arva SEO account uses this address. ' +
                    (t.context?.unverified_sender
                      ? 'The email couldn’t be verified as coming from it, so check before acting. '
                      : '')) +
                'They get your reply by email, and can answer it or use the private link in it.'
              : 'The workspace was closed.'}
        </p>
        <Context t={t} />
      </aside>
    )
  }
  const since = c.created_at
    ? Math.max(0, Math.round((now - new Date(c.created_at).getTime()) / 86400000))
    : null
  return (
    <aside className="ctx" aria-label="Customer">
      <div className="who">
        <Monogram name={c.name} />
        <div>
          <b>{c.name}</b>
          <small>{[c.owner?.name, c.owner?.email].filter(Boolean).join(' · ')}</small>
        </div>
      </div>
      <dl className="dl">
        <dt>Plan</dt>
        <dd>
          <PlanTag plan={c.plan.key} name={c.plan.name} yearly={c.interval === 'annual'} />
        </dd>
        <dt>Pays</dt>
        <dd>{c.mrr ? `${formatMoney(c.mrr)} a month` : 'Nothing yet'}</dd>
        <dt>Health</dt>
        <dd>
          <HealthBar value={c.health} />
        </dd>
        {c.country && (
          <>
            <dt>Country</dt>
            <dd>
              <CountryCode code={c.country} name={countryName(c.country)} /> {countryName(c.country)}
            </dd>
          </>
        )}
        <dt>Websites</dt>
        <dd>{numberFmt.format(c.websites)}</dd>
        <dt>Last active</dt>
        <dd>{c.last_active_at ? formatRelative(c.last_active_at) : 'Never'}</dd>
        {since !== null && (
          <>
            <dt>Customer for</dt>
            <dd>{since === 1 ? '1 day' : `${numberFmt.format(since)} days`}</dd>
          </>
        )}
      </dl>
      {c.payment_issue && (
        <div className="note" style={{ padding: '10px 12px', fontSize: 12.5 }}>
          <CreditCard aria-hidden="true" />
          <div>
            <b>Payment failing</b>Lemon Squeezy is retrying the card.
          </div>
        </div>
      )}
      {c.ending && !c.payment_issue && (
        <div className="note" style={{ padding: '10px 12px', fontSize: 12.5 }}>
          <Info aria-hidden="true" />
          <div>
            <b>Plan set to end</b>It runs until the end of the paid period.
          </div>
        </div>
      )}
      {t.project && (
        <p style={{ fontSize: 13, color: 'var(--muted)' }}>
          About <b style={{ color: 'var(--ink)' }}>{t.project.domain}</b>
        </p>
      )}
      {t.earlier.length > 0 && (
        <div>
          <h3 style={{ marginBottom: 8 }}>Earlier tickets</h3>
          <div className="feed">
            {t.earlier.map((e) => (
              <Link key={e.id} to={`/tickets/${e.id}`} className="fe" style={{ padding: '6px 0' }}>
                <span className="fe-t" style={{ fontSize: 12.5 }}>
                  {e.reference} · {e.subject}
                </span>
                <span className="fe-time">{statusLabel[e.status]}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
      <Context t={t} />
      <Link to={`/customers/${c.id}`} className="btn sm">
        <ArrowUpRight aria-hidden="true" />
        Open customer
      </Link>
    </aside>
  )
}

/** An emailed message: who sent it, to whom, and the whole email (the ticket keeps only the new part). */
function EmailDetails({ email }: { email: NonNullable<TicketDetail['thread'][number]['email']> }) {
  return (
    <details className="mt-2 text-xs">
      <summary className="cursor-pointer text-ink-subtle">
        By email from {email.from}
        {email.unverified && ' · not verified'}
      </summary>
      {email.unverified && (
        <p className="mt-1.5 text-ink-muted">
          It failed the sender’s DMARC check, so it may not really come from this address. Don’t share account
          details in your reply.
        </p>
      )}
      <dl className="mt-1.5 grid gap-1 text-ink-muted">
        {email.to && (
          <div>
            <dt className="inline">To: </dt>
            <dd className="inline break-all">{email.to}</dd>
          </div>
        )}
        {email.subject && (
          <div>
            <dt className="inline">Subject: </dt>
            <dd className="inline">{email.subject}</dd>
          </div>
        )}
      </dl>
      {email.text && (
        <pre className="relative mt-2 max-h-80 overflow-auto rounded-md border border-border p-2 font-sans text-xs whitespace-pre-wrap text-ink-muted">
          {email.text}
        </pre>
      )}
    </details>
  )
}

/** What the app sent with the ticket (the page, browser and website), when there is something. */
function Context({ t }: { t: TicketDetail }) {
  if (!t.context || Object.keys(t.context).length === 0) return null
  return (
    <details>
      <summary className="text-[11px] font-semibold tracking-[0.1em] text-ink-subtle uppercase">
        Sent with the ticket
      </summary>
      <dl className="mt-2 grid gap-1.5 text-xs">
        {Object.entries(t.context).map(([k, v]) => (
          <div key={k} className="grid gap-0.5">
            <dt className="text-ink-subtle">{k.replace(/_/g, ' ')}</dt>
            <dd className="font-mono break-all text-ink">{typeof v === 'string' ? v : JSON.stringify(v)}</dd>
          </div>
        ))}
      </dl>
    </details>
  )
}
